import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setEditableLayer } from "../../../../redux/action/Dashboard-action";
import { setFeatureTableOpen } from "../../../../redux/action/FeatureTable-actions";
import { ROLE_EDITOR } from "../../../../utils/helper";
import { DropdownMenu, DropdownMenuItem } from "../../../Dropdown";

import { checkLayerForFeatureTable } from "../../../../utils/featureTableUtils";
import { BiMapAltIcon, BsTableIcon, FaFileArchiveIcon } from "../../../Icons";
import { Tooltip } from "../../../UI";
import { StyledLayerItemButton } from "../../LayerList-styled";

export const IoDownload = (props) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 20 20"
    aria-hidden="true"
    height="14px"
    width="14px"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
      clipRule="evenodd"
    ></path>
  </svg>
);

const InformationIcon = (props) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 512 512"
    height="14px"
    width="14px"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="40"
      d="M196 220h64v172"
    ></path>
    <path
      fill="none"
      strokeLinecap="round"
      strokeMiterlimit="10"
      strokeWidth="40"
      d="M187 396h138"
    ></path>
    <path d="M256 160a32 32 0 1 1 32-32 32 32 0 0 1-32 32z"></path>
  </svg>
);

const ListDropdownMenu = ({
  config,
  disabled,
  groupedLayers,
  information,
  t,
}) => {
  const { view } = useSelector((state) => state.mapViewReducer);
  const activeModule = useSelector((state) => state.activeModule);
  const dispatch = useDispatch();

  const featureTableLayers = useMemo(() => {
    if (!Array.isArray(groupedLayers)) return [];

    const tableLayers = groupedLayers.filter((l) => {
      const { layerConfig } = l;
      if (!layerConfig) return false;
      const layer = view.map.layers.find((layer) => {
        return layer.layerConfig && layer.layerConfig.id === layerConfig.id;
      });

      return layer && checkLayerForFeatureTable(layer);
    });

    return tableLayers;
  }, [groupedLayers, view]);

  const showDataTable = useMemo(() => {
    return featureTableLayers.length > 0;
  }, [featureTableLayers]);

  const handleOpenDataTable = useCallback(() => {
    if (!Array.isArray(featureTableLayers)) return;
    const aliasesMap = new Set(featureTableLayers.map((layer) => layer.alias));
    let editableLayer;

    groupedLayers.forEach((l) => {
      aliasesMap.add(l.alias);
    });

    view.map.layers.forEach((layer) => {
      const layerConfig = layer.layerConfig;
      if (layerConfig && aliasesMap.has(layerConfig.alias)) {
        if (
          layer.layerModules &&
          layer.layerModules.includes(activeModule) &&
          !editableLayer
        ) {
          editableLayer = layer;
        }
      }
    });

    if (!editableLayer) {
      view.map.layers.forEach((layer) => {
        const layerConfig = layer.layerConfig;

        if (
          layerConfig &&
          aliasesMap.has(layerConfig.alias) &&
          !editableLayer
        ) {
          editableLayer = layer;
        }
      });
    }

    if (editableLayer) {
      dispatch(setEditableLayer(editableLayer));
      dispatch(setFeatureTableOpen(true));
    }
  }, [view, activeModule, featureTableLayers]);

  const tooltipContent = useMemo(() => {
    const menuArray = [];

    const isEditor = config.role === ROLE_EDITOR;

    const printArray = [
      {
        type: "csv",
        disabled: false,
        allowed: true,
        Icon: BsTableIcon,
      },
      {
        type: "shapefile",
        disabled: false,
        allowed: true,
        Icon: FaFileArchiveIcon,
      },
      {
        type: "geojson",
        disabled: false,
        allowed: true,
        Icon: BiMapAltIcon,
      },
    ].filter(({ allowed }) => allowed);

    printArray.forEach((item) => {
      menuArray.push(
        <DropdownMenuItem
          key="data-table"
          fillColor="#8a8a8a"
          style={{ fontSize: 12 }}
          onClick={handleOpenDataTable}
        >
          {<item.Icon width="14px" height="14px" />}
          <span>{item.type}</span>
        </DropdownMenuItem>
      );
    });

    // if (showDataTable) {
    //   menuArray.push(
    //     <DropdownMenuItem
    //       key="data-table"
    //       fillColor="#8a8a8a"
    //       style={{ fontSize: 12 }}
    //       onClick={handleOpenDataTable}
    //     >
    //       <svg
    //         xmlns="http://www.w3.org/2000/svg"
    //         viewBox="0 0 16 16"
    //         width="14px"
    //         height="14px"
    //       >
    //         <path d="M0 1v14h16V1zm1 5h2v2H1zm8 2H4V6h5zm0 1v2H4V9zM1 9h2v2H1zm0 5v-2h2v2zm3 0v-2h5v2zm11 0h-5v-2h5zm0-3h-5V9h5zm0-3h-5V6h5zm0-3H1V2h14z" />
    //       </svg>
    //       <span>{t("screen.widget.LayerList.layerDropdown.seeDataTable")}</span>
    //     </DropdownMenuItem>
    //   );
    // }

    // if (information) {
    //   menuArray.push(
    //     <Tooltip key="information" showOnClick content={information}>
    //       <DropdownMenuItem style={{ fontSize: 12, width: "100%" }}>
    //         <InformationIcon width="14" height="14" />
    //         <span>
    //           {t("screen.widget.LayerList.layerDropdown.information")}
    //         </span>
    //       </DropdownMenuItem>
    //     </Tooltip>
    //   );
    // }

    if (menuArray.length > 0) {
      return (
        <DropdownMenu style={{ minWidth: 80, boxShadow: "none" }}>
          {menuArray}
        </DropdownMenu>
      );
    }

    return null;
  }, [showDataTable, handleOpenDataTable, information]);

  return (
    <Tooltip
      padding="2px"
      placement="right"
      disabled={disabled}
      content={tooltipContent}
      showOnClick={true}
    >
      <StyledLayerItemButton>
        {/* <InformationIcon fill="#8a8a8a" /> */}
        <IoDownload fill="#8a8a8a" />
      </StyledLayerItemButton>
    </Tooltip>
  );
};

export default ListDropdownMenu;
